import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { handleApiError } from "@/lib/api-client";
import { authClient } from "@/lib/auth-client";
import {
	useAllowedDomains,
	useAddDomain,
	useDeleteDomain,
} from "@/hooks/useDomainQuery";

export const Route = createFileRoute("/_layout/settings/domains")({
	component: DomainsSettings,
	beforeLoad: async () => {
		// Check if user is authenticated
		const session = await authClient.getSession();
		if (!session) {
			throw new Error("Authentication required");
		}
	},
});

type AllowedDomain = {
	id: string;
	userId: string;
	domain: string;
	createdAt: Date | string;
};

function DomainsSettings() {
	const [newDomain, setNewDomain] = useState("");

	// Fetch domains using custom hook
	const { data: domainsData, isLoading, error } = useAllowedDomains();

	const domains = domainsData?.domains || [];
	const limit = domainsData?.limit || 10;

	// Show error toast if query fails
	if (error) {
		handleApiError(error, "Failed to load domains");
	}

	// Mutations using custom hooks
	const addDomainMutation = useAddDomain();
	const deleteDomainMutation = useDeleteDomain();

	const handleAddDomain = (e: React.FormEvent) => {
		e.preventDefault();

		if (!newDomain.trim()) {
			toast.error("Please enter a domain");
			return;
		}

		// Basic domain validation
		const domainPattern = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/;
		if (!domainPattern.test(newDomain.toLowerCase())) {
			toast.error("Invalid domain format", {
				description:
					"Please enter a valid domain like example.com (without www or https://)",
			});
			return;
		}

		addDomainMutation.mutate(newDomain.toLowerCase(), {
			onSuccess: () => {
				// Clear input field on success
				setNewDomain("");
			},
		});
	};

	const handleDeleteDomain = (domainId: string, domain: string) => {
		deleteDomainMutation.mutate({ domainId, domain });
	};

	if (isLoading) {
		return (
			<div className="container mx-auto max-w-4xl px-4 py-8">
				<div className="animate-pulse space-y-4">
					<div className="h-8 w-48 bg-gray-200 dark:bg-gray-800 rounded" />
					<div className="h-32 bg-gray-200 dark:bg-gray-800 rounded" />
				</div>
			</div>
		);
	}

	return (
		<div className="container mx-auto max-w-4xl px-4 py-8 space-y-8">
			{/* Header */}
			<div>
				<h1 className="text-3xl font-bold tracking-tight">Allowed Domains</h1>
				<p className="text-muted-foreground mt-2">
					Manage domains that can embed your stations. Only domains listed here
					can use the embed feature.
				</p>
			</div>

			{/* Add Domain Form */}
			<Card className="p-6">
				<h2 className="text-lg font-semibold mb-4">Add New Domain</h2>
				<form onSubmit={handleAddDomain} className="space-y-4">
					<div>
						<label
							htmlFor="domain"
							className="block text-sm font-medium mb-2"
						>
							Domain Name
						</label>
						<input
							id="domain"
							type="text"
							value={newDomain}
							onChange={(e) => setNewDomain(e.target.value)}
							placeholder="example.com"
							className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
							disabled={addDomainMutation.isPending || domains.length >= limit}
						/>
						<p className="text-sm text-muted-foreground mt-1">
							Enter domain without www or https:// (e.g., example.com)
						</p>
						<p className="text-sm text-muted-foreground mt-1">
							Both example.com and www.example.com will be automatically allowed
						</p>
					</div>

					<div className="flex items-center justify-between">
						<span className="text-sm text-muted-foreground">
							{domains.length} / {limit} domains used
						</span>
						<Button
							type="submit"
							disabled={addDomainMutation.isPending || domains.length >= limit}
							className="min-w-[100px]"
						>
							{addDomainMutation.isPending ? "Adding..." : "Add Domain"}
						</Button>
					</div>

					{domains.length >= limit && (
						<div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900 rounded-md p-3">
							<p className="text-sm text-yellow-800 dark:text-yellow-200">
								You've reached the maximum of {limit} domains. Remove a domain
								to add a new one.
							</p>
						</div>
					)}
				</form>
			</Card>

			{/* Domains List */}
			<Card className="p-6">
				<h2 className="text-lg font-semibold mb-4">Your Allowed Domains</h2>

				{domains.length === 0 ? (
					<div className="text-center py-8 text-muted-foreground">
						<p>No domains added yet.</p>
						<p className="text-sm mt-2">
							Add a domain above to allow it to embed your stations.
						</p>
					</div>
				) : (
					<div className="space-y-2">
						{domains.map((domain) => (
							<div
								key={domain.id}
								className="flex items-center justify-between p-4 border rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
							>
								<div className="flex-1">
									<p className="font-medium">{domain.domain}</p>
									<p className="text-sm text-muted-foreground">
										Added {new Date(domain.createdAt).toLocaleDateString()}
									</p>
								</div>
								<Button
									variant="destructive"
									size="sm"
									onClick={() => handleDeleteDomain(domain.id, domain.domain)}
									disabled={deleteDomainMutation.isPending}
								>
									{deleteDomainMutation.isPending ? "Removing..." : "Remove"}
								</Button>
							</div>
						))}
					</div>
				)}
			</Card>

			{/* Info Section */}
			<Card className="p-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900">
				<h3 className="font-semibold mb-2 text-blue-900 dark:text-blue-100">
					How it works
				</h3>
				<ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
					<li>• Add domains you want to allow embedding from</li>
					<li>
						• Both the plain domain and www subdomain will be automatically
						allowed
					</li>
					<li>• Playground domains (CodePen, JSFiddle, etc.) are always allowed</li>
					<li>• You can whitelist up to {limit} custom domains</li>
					<li>• Remove domains anytime to revoke embed access</li>
				</ul>
			</Card>
		</div>
	);
}
